import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Plus, X } from 'lucide-react';
import { Card, CardHeader, CardBody, CardFooter } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { Textarea } from '@/components/ui/Textarea';
import { useAuthStore } from '@/stores/authStore';
import api from '@/lib/api';
import type { ApiResponse, UserWithProfile } from '@hirenfire/shared';

export function EditProfilePage() {
  const navigate = useNavigate();
  const user = useAuthStore((s) => s.user);
  const setUser = useAuthStore((s) => s.setUser);
  const profile = user?.profile;

  const [displayName, setDisplayName] = useState(profile?.displayName ?? '');
  const [bio, setBio] = useState(profile?.bio ?? '');
  const [country, setCountry] = useState(profile?.country ?? '');
  const [availability, setAvailability] = useState(profile?.availability ?? '');
  const [hourlyRate, setHourlyRate] = useState(profile?.hourlyRate?.toString() ?? '');
  const [skills, setSkills] = useState<string[]>(profile?.skills ?? []);
  const [languages, setLanguages] = useState<string[]>(profile?.languages ?? []);
  const [devices, setDevices] = useState<string[]>(profile?.devices ?? []);
  const [isPublic, setIsPublic] = useState(profile?.isPublic ?? true);
  const [isAnonymous, setIsAnonymous] = useState(profile?.isAnonymous ?? false);

  const [newSkill, setNewSkill] = useState('');
  const [newLanguage, setNewLanguage] = useState('');
  const [newDevice, setNewDevice] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');

  const addToList = (
    list: string[],
    setList: (l: string[]) => void,
    value: string,
    setValue: (v: string) => void
  ) => {
    if (value.trim() && !list.includes(value.trim())) {
      setList([...list, value.trim()]);
      setValue('');
    }
  };

  const removeFromList = (
    list: string[],
    setList: (l: string[]) => void,
    index: number
  ) => {
    setList(list.filter((_, i) => i !== index));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setIsLoading(true);

    try {
      const res = await api.put<ApiResponse<UserWithProfile>>('/profile', {
        displayName,
        bio: bio || null,
        country: country || null,
        availability: availability || null,
        hourlyRate: hourlyRate ? Number(hourlyRate) : null,
        skills,
        languages,
        devices,
        isPublic,
        isAnonymous,
      });
      if (res.data.data) {
        setUser(res.data.data);
        navigate('/profile/me');
      }
    } catch (err: unknown) {
      const message =
        (err as { response?: { data?: { error?: { message?: string } } } })?.response?.data
          ?.error?.message ?? 'Failed to update profile.';
      setError(message);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="space-y-6 animate-fade-in max-w-3xl">
      <button
        onClick={() => navigate(-1)}
        className="flex items-center gap-2 text-gray-400 hover:text-white transition-colors"
      >
        <ArrowLeft className="w-4 h-4" />
        Back
      </button>

      <h1 className="text-2xl font-bold text-white">Edit Profile</h1>

      <form onSubmit={handleSubmit}>
        <Card>
          <CardHeader>Profile Information</CardHeader>
          <CardBody className="space-y-5">
            {error && (
              <div className="p-3 bg-error-500/10 border border-error-500/30 rounded-lg text-error-400 text-sm">
                {error}
              </div>
            )}

            <Input
              label="Display Name"
              placeholder="Your display name"
              value={displayName}
              onChange={(e) => setDisplayName(e.target.value)}
              required
            />

            <Textarea
              label="Bio"
              placeholder="Tell others about yourself..."
              value={bio}
              onChange={(e) => setBio(e.target.value)}
              maxChars={1000}
            />

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <Input
                label="Country"
                placeholder="e.g., United States"
                value={country}
                onChange={(e) => setCountry(e.target.value)}
              />
              <Input
                label="Availability"
                placeholder="e.g., Full-time, Part-time"
                value={availability}
                onChange={(e) => setAvailability(e.target.value)}
              />
            </div>

            <Input
              label="Hourly Rate (USD)"
              type="number"
              placeholder="25"
              value={hourlyRate}
              onChange={(e) => setHourlyRate(e.target.value)}
            />

            {/* Skills */}
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-1.5">Skills</label>
              <div className="flex gap-2 mb-2">
                <Input
                  placeholder="Add a skill..."
                  value={newSkill}
                  onChange={(e) => setNewSkill(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      e.preventDefault();
                      addToList(skills, setSkills, newSkill, setNewSkill);
                    }
                  }}
                />
                <Button
                  type="button"
                  variant="secondary"
                  onClick={() => addToList(skills, setSkills, newSkill, setNewSkill)}
                >
                  <Plus className="w-4 h-4" />
                </Button>
              </div>
              <div className="flex flex-wrap gap-2">
                {skills.map((s, i) => (
                  <span
                    key={i}
                    className="inline-flex items-center gap-1 px-2.5 py-1 bg-primary-500/10 text-primary-400 rounded-full text-sm"
                  >
                    {s}
                    <button
                      type="button"
                      onClick={() => removeFromList(skills, setSkills, i)}
                      className="hover:text-error-400"
                    >
                      <X className="w-3 h-3" />
                    </button>
                  </span>
                ))}
              </div>
            </div>

            {/* Languages */}
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-1.5">Languages</label>
              <div className="flex gap-2 mb-2">
                <Input
                  placeholder="Add a language..."
                  value={newLanguage}
                  onChange={(e) => setNewLanguage(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      e.preventDefault();
                      addToList(languages, setLanguages, newLanguage, setNewLanguage);
                    }
                  }}
                />
                <Button
                  type="button"
                  variant="secondary"
                  onClick={() => addToList(languages, setLanguages, newLanguage, setNewLanguage)}
                >
                  <Plus className="w-4 h-4" />
                </Button>
              </div>
              <div className="flex flex-wrap gap-2">
                {languages.map((l, i) => (
                  <span
                    key={i}
                    className="inline-flex items-center gap-1 px-2.5 py-1 bg-dark-600 text-gray-300 rounded-full text-sm"
                  >
                    {l}
                    <button
                      type="button"
                      onClick={() => removeFromList(languages, setLanguages, i)}
                      className="hover:text-error-400"
                    >
                      <X className="w-3 h-3" />
                    </button>
                  </span>
                ))}
              </div>
            </div>

            {/* Devices */}
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-1.5">Devices</label>
              <div className="flex gap-2 mb-2">
                <Input
                  placeholder="Add a device..."
                  value={newDevice}
                  onChange={(e) => setNewDevice(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      e.preventDefault();
                      addToList(devices, setDevices, newDevice, setNewDevice);
                    }
                  }}
                />
                <Button
                  type="button"
                  variant="secondary"
                  onClick={() => addToList(devices, setDevices, newDevice, setNewDevice)}
                >
                  <Plus className="w-4 h-4" />
                </Button>
              </div>
              <div className="flex flex-wrap gap-2">
                {devices.map((d, i) => (
                  <span
                    key={i}
                    className="inline-flex items-center gap-1 px-2.5 py-1 bg-dark-600 text-gray-300 rounded-full text-sm"
                  >
                    {d}
                    <button
                      type="button"
                      onClick={() => removeFromList(devices, setDevices, i)}
                      className="hover:text-error-400"
                    >
                      <X className="w-3 h-3" />
                    </button>
                  </span>
                ))}
              </div>
            </div>

            {/* Toggles */}
            <div className="space-y-3">
              <label className="flex items-center gap-3 cursor-pointer">
                <input
                  type="checkbox"
                  checked={isPublic}
                  onChange={(e) => setIsPublic(e.target.checked)}
                  className="w-4 h-4 rounded bg-dark-700 border-dark-500 text-primary-500 focus:ring-primary-500/50"
                />
                <span className="text-sm text-gray-300">Make profile public</span>
              </label>
              <label className="flex items-center gap-3 cursor-pointer">
                <input
                  type="checkbox"
                  checked={isAnonymous}
                  onChange={(e) => setIsAnonymous(e.target.checked)}
                  className="w-4 h-4 rounded bg-dark-700 border-dark-500 text-primary-500 focus:ring-primary-500/50"
                />
                <span className="text-sm text-gray-300">Hide real name (use display name only)</span>
              </label>
            </div>
          </CardBody>
          <CardFooter>
            <div className="flex justify-end gap-3">
              <Button variant="secondary" type="button" onClick={() => navigate(-1)}>
                Cancel
              </Button>
              <Button type="submit" isLoading={isLoading}>
                Save Changes
              </Button>
            </div>
          </CardFooter>
        </Card>
      </form>
    </div>
  );
}
